import React, { useState } from 'react';
import { useAnalytics } from '../hooks/useAnalytics';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';

interface FormData {
  name: string;
  email: string;
}

/**
 * Example component showing how to use Firebase Analytics
 * This demonstrates various ways to track user interactions
 */
const ExampleWithAnalytics: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [formData, setFormData] = useState<FormData>({ name: '', email: '' });
  
  // Get analytics functions from the hook
  const {
    trackButtonClick,
    trackSearch,
    trackFeatureUsage,
    trackEvent
  } = useAnalytics();

  // Handle button click with analytics
  const handleButtonClick = (buttonName: string) => {
    trackButtonClick(buttonName, 'example-component');
    // Your button logic here
  };

  // Handle search with analytics
  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchTerm.trim()) {
      // Track the search
      trackSearch(searchTerm, 5); // Assuming 5 results found
      
      // Your search logic here
      setSearchTerm('');
    }
  };

  // Handle form submission with analytics
  const handleFormSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      // Your form submission logic here
      
      // Track successful submission
      trackEvent('form_submitted', {
        form_name: 'contact-form',
        success: true
      });
      
      // Reset form
      setFormData({ name: '', email: '' });
      
      alert('Form submitted successfully!');
    } catch (error) {
      // Track failed submission
      trackEvent('form_submitted', {
        form_name: 'contact-form',
        success: false
      });
      // Form submission failed
    }
  };

  // Handle content interaction
  const handleContentClick = (contentId: string) => {
    trackEvent('content_engagement', {
      content_type: 'article',
      content_id: contentId,
      action: 'view'
    });
    // Content viewed
  };

  // Handle feature usage
  const handleFeatureClick = (featureName: string) => {
    trackFeatureUsage(featureName, 'used', {
      user_type: 'free', // or 'premium'
      timestamp: new Date().toISOString()
    });
    // Feature used
  };

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <h1 className="text-2xl font-bold mb-6">Analytics Example Component</h1>
      
      {/* Button Analytics Example */}
      <section className="mb-8">
        <h2 className="text-xl font-semibold mb-4">Button Click Tracking</h2>
        <div className="space-x-4">
          <Button
            onClick={() => handleButtonClick('cta-button')}
          >
            Call to Action
          </Button>

          <Button
            variant="secondary"
            onClick={() => handleButtonClick('feature-button')}
          >
            Try Feature
          </Button>

          <Button
            variant="outline"
            onClick={() => handleFeatureClick('premium-feature')}
          >
            Premium Feature
          </Button>
        </div>
      </section>

      {/* Search Analytics Example */}
      <section className="mb-8">
        <h2 className="text-xl font-semibold mb-4">Search Tracking</h2>
        <form onSubmit={handleSearch} className="flex gap-2">
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search something..."
            className="flex-1 px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          <button 
            type="submit"
            className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600"
          >
            Search
          </button>
        </form>
      </section>

      {/* Form Analytics Example */}
      <section className="mb-8">
        <h2 className="text-xl font-semibold mb-4">Form Submission Tracking</h2>
        <form onSubmit={handleFormSubmit} className="space-y-4 max-w-md">
          <div>
            <Label htmlFor="name" className="block mb-1">Name:</Label>
            <Input
              id="name"
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              required
            />
          </div>

          <div>
            <Label htmlFor="email" className="block mb-1">Email:</Label>
            <Input
              id="email"
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              required
            />
          </div>

          <Button
            type="submit"
            variant="secondary"
          >
            Submit Form
          </Button>
        </form>
      </section>

      {/* Content Engagement Example */}
      <section className="mb-8">
        <h2 className="text-xl font-semibold mb-4">Content Engagement Tracking</h2>
        <div className="space-y-2">
          <div 
            className="p-4 border border-gray-200 rounded cursor-pointer hover:bg-gray-50"
            onClick={() => handleContentClick('article-1')}
          >
            <h3 className="font-medium">Article 1: Getting Started with Analytics</h3>
            <p className="text-gray-600">Click to view this article</p>
          </div>
          
          <div 
            className="p-4 border border-gray-200 rounded cursor-pointer hover:bg-gray-50"
            onClick={() => handleContentClick('article-2')}
          >
            <h3 className="font-medium">Article 2: Advanced Tracking Techniques</h3>
            <p className="text-gray-600">Click to view this article</p>
          </div>
        </div>
      </section>

      {/* Custom Event Example */}
      <section className="mb-8">
        <h2 className="text-xl font-semibold mb-4">Custom Event Tracking</h2>
        <Button
          variant="destructive"
          onClick={() => trackEvent('custom_interaction', {
            component: 'example-component',
            interaction_type: 'custom_button',
            value: 100
          })}
        >
          Track Custom Event
        </Button>
      </section>
    </div>
  );
};

export default ExampleWithAnalytics; 